import { useEffect, useMemo, useRef } from 'react';
import { GAME_MODES } from '../game/modes';
import { readHighScore, readModeStats } from '../game/storage';

interface StatsDialogProps {
  revision: number;
  onClose: () => void;
}

export function StatsDialog({ revision, onClose }: StatsDialogProps) {
  const closeButton = useRef<HTMLButtonElement>(null);
  const modeStats = useMemo(() => {
    void revision;
    return GAME_MODES.map((mode) => ({
      mode,
      stats: readModeStats(mode.id),
      bestScore: readHighScore(mode.id),
    }));
  }, [revision]);
  const totals = modeStats.reduce((sum, entry) => ({
    games: sum.games + entry.stats.games,
    score: sum.score + entry.stats.totalScore,
    lines: sum.lines + entry.stats.totalLines,
    quads: sum.quads + entry.stats.quads,
  }), { games: 0, score: 0, lines: 0, quads: 0 });

  useEffect(() => {
    closeButton.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="settings-dialog stats-dialog" role="dialog" aria-modal="true" aria-labelledby="stats-title" onMouseDown={(event) => event.stopPropagation()}>
        <header className="settings-header">
          <div>
            <span className="overlay-kicker">Your history</span>
            <h2 id="stats-title">Local stats</h2>
          </div>
          <button ref={closeButton} type="button" className="dialog-close" onClick={onClose} aria-label="Close local stats"><span aria-hidden="true">&times;</span></button>
        </header>

        <div className="local-save-note">
          <span className="local-save-note__dot" aria-hidden="true" />
          <div>
            <strong>Saved locally on this device</strong>
            <p>Nothing is uploaded or shared. Clearing this browser's site data will erase these stats.</p>
          </div>
        </div>

        <section className="stats-summary" aria-label="Lifetime totals">
          <div><span>Runs</span><strong>{totals.games.toLocaleString()}</strong></div>
          <div><span>Total score</span><strong>{totals.score.toLocaleString()}</strong></div>
          <div><span>Lines</span><strong>{totals.lines.toLocaleString()}</strong></div>
          <div><span>Quads</span><strong>{totals.quads.toLocaleString()}</strong></div>
        </section>

        <div className="stats-section-heading">
          <div><h3>By mode</h3><p>Current runs are added as you play and finalized when they end.</p></div>
        </div>
        <section className="mode-stats-grid" aria-label="Statistics by game mode">
          {modeStats.map(({ mode, stats, bestScore }) => (
            <article className="mode-stats-card" key={mode.id}>
              <header><strong>{mode.name}</strong><small>{mode.shortDescription}</small></header>
              <dl>
                <div><dt>Best score</dt><dd>{bestScore.toLocaleString()}</dd></div>
                <div><dt>Best lines</dt><dd>{stats.bestLines.toLocaleString()}</dd></div>
                <div><dt>Runs</dt><dd>{stats.games.toLocaleString()}</dd></div>
                <div><dt>Total lines</dt><dd>{stats.totalLines.toLocaleString()}</dd></div>
                <div><dt>Quads</dt><dd>{stats.quads.toLocaleString()}</dd></div>
              </dl>
            </article>
          ))}
        </section>

        <button type="button" className="primary-button settings-done" onClick={onClose}>Done</button>
      </section>
    </div>
  );
}
